import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { trackPageView } from "../utility/analytics.utils";

/**
 * Map route paths to readable page names for analytics
 */
const getPageName = (pathname: string): string => {
  // Extract the route pattern
  const path = pathname.split("?")[0]; // Remove query params for matching
  
  // Route to page name mapping
  const routeMap: Record<string, string> = {
    "/": "Home",
    "/explore": "Explore",
    "/auth": "Auth",
    "/about": "About",
    "/contact": "Contact",
    "/my-stories": "My Stories",
    "/my-bookmarks": "My Bookmarks",
    "/dashboard": "Dashboard",
    "/edit-profile": "Edit Profile",
    "/create-story": "Create Story",
    "/manage-story": "Story Management",
    "/privacy-policy": "Privacy Policy",
    "/terms-of-service": "Terms of Service",
    "/content-guidelines": "Content Guidelines",
    "/cookie-policy": "Cookie Policy",
    "/copyright-policy": "Copyright Policy",
    "/data-rights": "Data Rights",
    "/acceptable-use-policy": "Acceptable Use Policy",
    "/careers": "Careers",
    "/whats-new": "What's New",
    "/challenges/ink-masters": "Ink Masters",
    "/challenges/ink-masters/create": "Create Ink Master",
  };

  // Check for dynamic routes
  if (path.startsWith("/story/") && path.includes("/chapter/")) {
    return "Chapter Details";
  }
  if (path.startsWith("/story/")) {
    return "Story Details";
  }
  if (path.startsWith("/user/")) {
    return "User Profile";
  }
  if (path.startsWith("/careers/") && !path.endsWith("/create-job")) {
    return "Job Details";
  }
  if (path.startsWith("/careers/create-job")) {
    return "Create Job";
  }
  if (path.startsWith("/challenges/ink-masters/edit/")) {
    return "Edit Ink Master";
  }
  if (path.startsWith("/challenges/ink-masters/")) {
    return "Ink Master Details";
  }

  // Return mapped name or use pathname as fallback
  return routeMap[path] || pathname;
};

/**
 * Hook to track page views on route changes
 * Should be used in the main App component
 * Automatically tracks all route changes with meaningful page names
 */
export const usePageTracking = (): void => {
  const location = useLocation();

  useEffect(() => {
    // Get meaningful page name
    const pageName = getPageName(location.pathname);
    
    // Get page title from document or use page name
    const pageTitle = document.title || pageName;
    
    // Track page view on route change
    trackPageView(pageName, pageTitle);
  }, [location]);
};

